"use client";

import '@/lib/i18n';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';

/** Единый порядок источников языка (K-0050): ?lang= → сохранённый → устройство → he.
    Ровно этот же порядок повторяет инлайн-скрипт в layout.tsx, который выставляет
    lang/dir на <html> до первой отрисовки — менять только вместе. */
function resolveTargetLang(): string {
  const supported = ['he', 'ru', 'en', 'fr'];
  const urlLang = new URLSearchParams(window.location.search).get('lang');
  if (urlLang && supported.includes(urlLang)) {
    localStorage.setItem('i18nextLng', urlLang); // явный ?lang= — осознанный выбор
    return urlLang;
  }
  const saved = localStorage.getItem('i18nextLng');
  if (saved && supported.includes(saved)) return saved;
  // Первый заход: язык устройства. Телефон на русском → русский без единого
  // клика (сценарий Дениса). Автоопределение не сохраняем.
  const navLang = (navigator.language || 'he').slice(0, 2).toLowerCase();
  return supported.includes(navLang) ? navLang : 'he';
}

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const { i18n } = useTranslation();

  useEffect(() => {
    const target = resolveTargetLang();
    if (target === 'he') return;

    // K-0050: прежний двойной requestAnimationFrame молча не срабатывал —
    // язык оставался ивритом до ручного клика по переключателю, хотя выбор
    // уже лежал в localStorage. setTimeout выполняется и в фоновой вкладке,
    // и не зависит от кадров отрисовки. Эффекты выполняются после коммита
    // гидратации, поэтому таймаут 0 уже безопасен; второй вызов — страховка,
    // если первый пришёлся на догидрацию отложенных Suspense-островов.
    let cancelled = false;
    const apply = () => {
      if (!cancelled && i18n.language !== target) {
        i18n.changeLanguage(target).catch(() => { /* остаёмся на иврите */ });
      }
    };
    const t1 = setTimeout(apply, 0);
    const t2 = setTimeout(apply, 400);
    return () => { cancelled = true; clearTimeout(t1); clearTimeout(t2); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // lang/dir на <html> — синхронно со сменой языка (RTL ↔ LTR).
    const html = document.documentElement;
    const newDir = i18n.language === 'he' ? 'rtl' : 'ltr';
    if (html.dir !== newDir) html.dir = newDir;
    if (html.lang !== i18n.language) html.lang = i18n.language;

    // Bridge language → native app shell so native tab-bar labels stay in sync.
    const rn = (window as unknown as { ReactNativeWebView?: { postMessage: (m: string) => void } }).ReactNativeWebView;
    if (rn) { try { rn.postMessage(JSON.stringify({ type: 'YOFI_LANG', lang: i18n.language })); } catch { /* ignore bridge errors */ } }
  }, [i18n.language]);

  return <>{children}</>;
}
